<!--
  "You may not do that" — mounted once in App.vue, driven by useAccessDenied.

  What it has to achieve: the operator knows WHAT was refused, understands that
  it is a permission and not a fault or an outage, and can say precisely what to
  ask for. Hence the permission key is shown verbatim: it is the string an admin
  types into Rollen & Rechte, and copying it beats describing the screen.
-->
<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[2400] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      :aria-label="t('accessDenied.title')"
      @click.self="close"
      @keydown.esc="close"
    >
      <div class="w-full max-w-[460px] rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div class="flex items-start gap-3 px-5 pt-5 pb-3">
          <span class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-50 text-amber-600">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </span>
          <div class="min-w-0 flex-1">
            <h3 class="m-0 text-[15px] font-bold text-gray-900">{{ t('accessDenied.title') }}</h3>
            <p class="mt-1 mb-0 text-[13px] text-gray-600 leading-relaxed">{{ headline }}</p>
          </div>
        </div>

        <div class="px-5 pb-4 space-y-2.5">
          <!-- What an admin needs in order to grant it. -->
          <div v-if="info?.pageKey" class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
            <div class="text-[11px] font-bold uppercase tracking-wide text-gray-500">{{ t('accessDenied.neededLabel') }}</div>
            <div class="mt-1 flex items-center gap-2 min-w-0">
              <code class="text-[12.5px] font-semibold text-gray-900 truncate">{{ info.pageKey }}</code>
              <span class="shrink-0 text-[11px] font-semibold text-gray-500">· {{ actionWord }}</span>
              <button
                type="button"
                class="ml-auto shrink-0 text-[11.5px] font-semibold text-gray-600 hover:text-gray-900 bg-transparent border-0 cursor-pointer"
                @click="copyKey"
              >{{ copied ? t('common.copied') : t('common.copy') }}</button>
            </div>
          </div>

          <p class="m-0 text-[12.5px] text-gray-500 leading-relaxed">{{ t('accessDenied.askAdmin') }}</p>

          <!-- The server's own words, for a developer reading over a shoulder.
               Collapsed, because it is never the explanation the operator needs. -->
          <details v-if="info?.detail" class="group">
            <summary class="cursor-pointer text-[11.5px] text-gray-400 hover:text-gray-600 select-none">{{ t('accessDenied.technicalDetails') }}</summary>
            <pre class="mt-1.5 mb-0 max-h-28 overflow-auto rounded-md bg-gray-50 border border-gray-200 p-2 text-[11px] text-gray-600 whitespace-pre-wrap break-words">{{ info.detail }}</pre>
          </details>
        </div>

        <div class="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
          <BaseButton variant="primary" size="sm" @click="close">{{ t('common.ok') }}</BaseButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseButton from '@/components/ui/BaseButton.vue'
import { useAccessDenied } from '@/composables/useAccessDenied'

const { t } = useI18n()
const { open, info, closeAccessDenied } = useAccessDenied()

const copied = ref(false)

const actionWord = computed(() => t(`accessDenied.action.${info.value?.action || 'edit'}`))

/**
 * "Du darfst <X> hier nicht <bearbeiten>." — the subject is named when the
 * caller knew it, because "you may not edit" alone leaves the operator
 * guessing which of the three things they just touched was refused.
 */
const headline = computed(() => {
  if (!info.value) return ''
  return info.value.subject
    ? t('accessDenied.bodyWithSubject', { action: actionWord.value, subject: info.value.subject })
    : t('accessDenied.body', { action: actionWord.value })
})

function close() {
  closeAccessDenied()
}

async function copyKey() {
  const key = info.value?.pageKey
  if (!key) return
  try {
    await navigator.clipboard.writeText(key)
    copied.value = true
    setTimeout(() => { copied.value = false }, 1500)
  } catch {
    // A blocked clipboard is not worth an error on top of an error; the key is
    // on screen and can be read out.
  }
}

// Reset the copied flag when a new refusal replaces an old one.
watch(open, (v) => { if (!v) copied.value = false })
</script>
